import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useTokenAndUser } from "./action/userData.jsx";
import { useGlobalData } from "../context/GlobalDataContext";
import { f7ready } from "framework7-react";
import api from "../library/Axios.jsx";

const CHAT_SDK_CSS = "https://sdk.chatgamer.chat/chat-sdk.css";
const CHAT_SDK_SCRIPT = "https://sdk.chatgamer.chat/chat-sdk.umd.cjs";
const DEFAULT_CHAT_API = "https://api.chatgamer.chat/api/v1";
const CONTAINER_ID = "chat-sdk-container";
const GLOBAL_CONTAINER_ID = "chat-sdk-container-global";
const PAGE_LIVE_CHAT_ID = "page-live-chat";

/**
 * Chat SDK Manager – 與舊 widget 一樣：全域一個容器，只在 /live_chat 顯示，離開只 hidden，不重新 init。
 * 配置來自 global.livechat_settings.cgchat。
 */
const ChatSDKManager = () => {
  const { pathname } = useLocation();
  const { user, token, isLoading } = useTokenAndUser();
  const { globalData: global } = useGlobalData();
  const sdkRef = useRef(null);
  const scriptLoadedRef = useRef(false);
  const initDoneRef = useRef(false);
  const globalContainerRef = useRef(null);
  /** Last `${hasToken}:${userCodeOrGuest}` used by runInit — avoids destroy/re-init blink */
  const lastInitKeyRef = useRef("");
  const positionListenersCleanupRef = useRef(null);

  const getSDKConfig = () => {
    const cg = global?.livechat_settings?.cgchat || {};
    const apiUrl = cg.chat_api_url || DEFAULT_CHAT_API;
    const platformPublicKey =
      cg.public_key ||
      (typeof process !== "undefined" &&
        process.env?.REACT_APP_LIVECHAT_PUBLIC_KEY) ||
      "";
    const userTokenPath = "live_chat_2_user_token";

    return { apiUrl, platformPublicKey, userTokenPath };
  };

  const guestConfig = () => {
    const { apiUrl, platformPublicKey } = getSDKConfig();
    return {
      apiUrl,
      platformPublicKey,
      displayMode: "inline",
      container: `#${CONTAINER_ID}`,
      isGuest: true,
      userId: undefined,
      getUserToken: undefined,
      language: "en",
    };
  };

  const getUserId = (u) => u?.code;

  const userConfig = (userData) => {
    const { apiUrl, platformPublicKey, userTokenPath } = getSDKConfig();
    const uid = getUserId(userData);
    // 呼叫後端 live_chat_2_user_token（需 header: token, country-id），回傳 { userToken }
    return {
      apiUrl,
      platformPublicKey,
      displayMode: "inline",
      container: `#${CONTAINER_ID}`,
      isGuest: false,
      userId: uid,
      getUserToken: () => {
        return api
          .post(userTokenPath)
          .then((r) => r?.data?.userToken ?? r?.data?.user_token ?? null)
          .catch(() => undefined);
      },
      //! Copy params from Chatroom v1 (not tested yet)
      onNewMessage: function (message) {
        console.log(`[Chatroom onNewMessage] : ${message}`);
      },
      onUnreadCountChange: function (count) {
        console.log(`[Chatroom onUnreadCountChange] : ${count}`);
      },
      // onTransferToHuman: function (data) {},
      // onChannelClosed: function (data) {},
      // onRatingSubmitted: function (data) {},
      language: userData?.language || "en",
    };
  };

  const clearPositionListeners = () => {
    positionListenersCleanupRef.current?.();
    positionListenersCleanupRef.current = null;
  };

  const computeAndApplyPosition = (container) => {
    if (!container) return;
    const pageEl = document.getElementById(PAGE_LIVE_CHAT_ID);
    if (!pageEl) return;
    const r = pageEl.getBoundingClientRect();
    container.style.position = "fixed";
    container.style.top = `${Math.max(0, r.top)}px`;
    container.style.left = `${Math.max(0, r.left)}px`;
    container.style.width = `${Math.max(0, r.width)}px`;
    container.style.height = `${Math.max(0, r.height)}px`;
    container.style.right = "auto";
    container.style.bottom = "auto";
  };

  const resizeObserverRef = useRef(null);

  useEffect(() => {
    let pageEl;
    let interval = null;
    const scheduleAddObserver = () => {
      pageEl = document.getElementById(PAGE_LIVE_CHAT_ID);

      if (pageEl) {
        if (!resizeObserverRef.current) {
          resizeObserverRef.current = new ResizeObserver((entries) => {
            entries.forEach((entry) => {
              computeAndApplyPosition(globalContainerRef.current);
            });
          });

          resizeObserverRef.current.observe(pageEl);
          if (interval) {
            clearInterval(interval);
          }
        }
      } else {
        // console.log("pageEl not exist");
      }
    };

    interval = setInterval(() => {
      scheduleAddObserver();
    }, 500);

    return () => {
      if (pageEl && resizeObserverRef.current?.unobserve) {
        resizeObserverRef.current.unobserve(pageEl);
      }
    };
  }, [pathname]);

  const attachPositionListeners = (container) => {
    clearPositionListeners();
    if (!container) return;
    const pageEl = document.getElementById(PAGE_LIVE_CHAT_ID);
    if (!pageEl) return;

    let rafId = 0;
    const schedule = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        computeAndApplyPosition(globalContainerRef.current);
      });
    };

    schedule();

    const onResize = () => schedule();

    window.addEventListener("resize", onResize);
    window.visualViewport?.addEventListener("resize", onResize);

    let scrollEl = pageEl.parentElement;
    while (scrollEl && scrollEl !== document.body) {
      const oy = getComputedStyle(scrollEl).overflowY;
      if (/(auto|scroll|overlay)/.test(oy)) break;
      scrollEl = scrollEl.parentElement;
    }
    if (scrollEl && scrollEl !== document.body) {
      scrollEl.addEventListener("scroll", schedule, { passive: true });
    } else {
      window.addEventListener("scroll", schedule, true);
      scrollEl = null;
    }

    positionListenersCleanupRef.current = () => {
      window.removeEventListener("resize", onResize);
      window.visualViewport?.removeEventListener("resize", onResize);
      if (scrollEl) {
        scrollEl.removeEventListener("scroll", schedule);
      } else {
        window.removeEventListener("scroll", schedule, true);
      }
    };
  };

  const initSDK = (config) => {
    const ChatRoom = window.ChatRoom;
    if (!ChatRoom || typeof ChatRoom.init !== "function") return null;
    if (sdkRef.current) {
      try {
        sdkRef.current.destroy();
      } catch (e) { }
      sdkRef.current = null;
    }
    sdkRef.current = ChatRoom.init(config);
    return sdkRef.current;
  };

  const runInit = () => {
    const { platformPublicKey } = getSDKConfig();
    if (!platformPublicKey) return;
    const userId = getUserId(user);
    const isUser = !!token;
    const config = isUser ? userConfig(user) : guestConfig();
    console.log("[ChatSDKManager] runInit", {
      isGuest: !isUser,
      isUser,
      userId,
      hasToken: !!token,
      token: token ? `${String(token).slice(0, 8)}...` : null,
      hasGetUserToken: !!config.getUserToken,
      userKeys: user ? Object.keys(user) : [],
    });
    initSDK(config);
    initDoneRef.current = true;
    const uid = getUserId(user);
    lastInitKeyRef.current = `${!!token}:${uid ?? "guest"}`;
  };

  const showContainer = () => {
    const el =
      globalContainerRef.current ||
      document.getElementById(GLOBAL_CONTAINER_ID);
    if (!el) return;
    if (!document.getElementById(PAGE_LIVE_CHAT_ID)) {
      if (
        typeof window !== "undefined" &&
        !window.location.pathname.startsWith("/live_chat")
      )
        return;
      requestAnimationFrame(() => showContainer());
      return;
    }
    computeAndApplyPosition(el);
    attachPositionListeners(el);
    el.style.display = "block";
    el.style.visibility = "visible";
    el.style.opacity = "1";
    el.style.pointerEvents = "auto";
    el.style.zIndex = "5000";
  };

  const hideContainer = () => {
    clearPositionListeners();
    const el =
      globalContainerRef.current ||
      document.getElementById(GLOBAL_CONTAINER_ID);
    if (!el) return;
    el.style.visibility = "hidden";
    el.style.opacity = "0";
    el.style.pointerEvents = "none";
    el.style.zIndex = "-1";
    el.style.display = "none";
  };

  useEffect(() => {
    if (global?.theme === "blank" || isLoading) return;

    const { platformPublicKey } = getSDKConfig();
    if (!platformPublicKey) return;

    f7ready(() => {
      const existing = document.getElementById(GLOBAL_CONTAINER_ID);
      if (existing) {
        globalContainerRef.current = existing;
        if (window.location.pathname.startsWith("/live_chat")) showContainer();
        else hideContainer();
        return () => {
          clearPositionListeners();
        };
      }

      if (typeof window.process === "undefined") {
        window.process = { env: { NODE_ENV: "production" } };
      }

      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = CHAT_SDK_CSS;
      document.head.appendChild(link);

      const globalContainer = document.createElement("div");
      globalContainer.id = GLOBAL_CONTAINER_ID;
      globalContainer.style.cssText =
        "position:fixed;left:0;width:100vw;visibility:hidden;opacity:0;pointer-events:none;z-index:-1;display:none;transition:opacity 0.2s ease-in-out, visibility 0.2s ease-in-out;background:transparent;";

      const inner = document.createElement("div");
      inner.id = CONTAINER_ID;
      inner.style.cssText =
        "width:100%;height:100%;overflow:hidden;min-height:0;";
      globalContainer.appendChild(inner);

      document.body.appendChild(globalContainer);
      globalContainerRef.current = globalContainer;

      const script = document.createElement("script");
      script.src = CHAT_SDK_SCRIPT;
      script.async = true;
      script.onload = () => {
        scriptLoadedRef.current = true;
        console.log("[ChatSDKManager] script onload, first init", {
          userId: user?.id,
          hasToken: !!token,
        });
        if (!initDoneRef.current) runInit();
        if (window.location.pathname.startsWith("/live_chat")) showContainer();
      };
      script.onerror = () => {
        console.error("[ChatSDKManager] Failed to load chat-sdk script");
      };
      document.body.appendChild(script);

      return () => {
        clearPositionListeners();
      };
    });
  }, [global, isLoading]);

  useEffect(() => {
    if (pathname.startsWith("/live_chat")) {
      showContainer();
    } else {
      hideContainer();
    }
  }, [pathname]);

  // 登入／登出後重新 init（guest ⇄ user 切換）
  useEffect(() => {
    const uid = getUserId(user);
    console.log("[ChatSDKManager] effect user/token", {
      userId: uid,
      userKeys: user ? Object.keys(user) : [],
      hasToken: !!token,
      scriptLoaded: scriptLoadedRef.current,
      initDone: initDoneRef.current,
      hasChatRoom: !!window.ChatRoom,
    });
    if (!window.ChatRoom || !scriptLoadedRef.current || !initDoneRef.current)
      return;
    const nextKey = `${!!token}:${uid ?? "guest"}`;
    if (nextKey === lastInitKeyRef.current) return;
    runInit();
  }, [user?.id, user?.user_id, user?.member_id, token]);

  useEffect(() => {
    window.reinitChatSDKAsGuest = () => {
      if (!window.ChatRoom) return;
      initSDK(guestConfig());
    };
    window.reinitChatSDKAsUser = (userData) => {
      if (!userData?.id || !window.ChatRoom) return;
      initSDK(userConfig(userData));
    };
    return () => {
      clearPositionListeners();
      delete window.reinitChatSDKAsGuest;
      delete window.reinitChatSDKAsUser;
      if (sdkRef.current) {
        try {
          sdkRef.current.destroy();
        } catch (e) { }
        sdkRef.current = null;
      }
      initDoneRef.current = false;
      const el = document.getElementById(GLOBAL_CONTAINER_ID);
      if (el) el.remove();
      globalContainerRef.current = null;
    };
  }, []);

  if (typeof window !== "undefined" && window.__PRERENDER__) {
    return null;
  }
  return null;
};

export default ChatSDKManager;
